/**
 * OrderDetailsModal - Modal de detalles completos del pedido
 *
 * Modal que muestra información detallada de un pedido específico.
 * Incluye:
 * - Información completa del cliente y tienda
 * - Lista de productos con cantidades y estado
 * - Fecha de creación del pedido
 * - Estado de preparación
 * - Información de courier y tracking
 * - Diseño responsive y accesible
 */

import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { UserCard } from '@shared/components/ui/usercard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faCalendar } from '@fortawesome/pro-light-svg-icons';
import { type OrderDetails } from '../../models';
import { usePrintOrder } from '../../hooks/usePrintOrder';

interface OrderDetailsModalProps {
  order?: OrderDetails;
}

export const OrderDetailsModal = forwardRef<ModalCenterRef, OrderDetailsModalProps>(({ order }, ref) => {
  const { t } = useTranslation('packingModule');
  const { printOrder, isOrderLoading } = usePrintOrder();

  if (!order) return null;

  const handlePrint = async () => {
    if (order.id) {
      await printOrder(parseInt(order.id));
    }
  };

  return (
    <ModalCenter ref={ref} title="" type="default" width="lg">
      <div className="space-y-6">
        {/* Customer Information Section - Información del Cliente */}
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 justify-between pr-7 pl-0 mb-2">
            <div>
              {/* Customer Info - Información del Cliente */}
              <UserCard
                name={order.customerName}
                email={order.orderNumber}
                avatarUrl={order.customerLogo}
                data-testid="customer-user-card"
              />
              {/* Status Badge - Badge de Estado */}
              <Badge
                variant={order.status === 'prepared' ? 'default' : 'outline'}
                className={`text-xs w-fit flex items-center gap-2 mt-1 border-0 ${
                  order.status === 'prepared' || order.statusId === 6
                    ? 'bg-bg-success text-success'
                    : 'bg-card-border text-badge-status-3'
                }`}
              >
                <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
                {order.statusId === 6
                  ? // Pedido packeado - mostrar fecha de processed_at o lastShipmentUpdate
                    (() => {
                      const dateToShow = order.processedAt || order.lastShipmentUpdate;
                      return dateToShow
                        ? new Date(dateToShow).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'Preparado';
                    })()
                  : order.status === 'prepared'
                    ? 'Preparado'
                    : t('orders.status_preparing')}
              </Badge>
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={isOrderLoading(parseInt(order.id))}
                data-testid="print-order-button"
              >
                <FontAwesomeIcon icon={faPrint} className="!w-3 !h-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Products Section - Sección de Productos */}
        <div className="space-y-3 !mt-0">
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {order.products.map((product, index) => (
              <div
                key={product.id}
                className={`flex items-center justify-between pb-3 ${
                  index < order.products.length - 1 ? 'border-b border-card-border mb-2' : ''
                }`}
              >
                {/* Product Info - Información del Producto */}
                <div className="flex items-center gap-3 flex-1">
                  <Product
                    id={parseInt(product.id)}
                    name={product.name}
                    src={product.image}
                    code={product.code}
                    showTooltip={true}
                    enableLineClamp={false}
                    data-testid={`modal-product-${product.id}`}
                  />
                </div>
                {/* Product Status and Quantity - Estado y Cantidad del Producto */}
                <div className="flex items-center gap-3">
                  {/* Quantity Badge - Badge de Cantidad */}
                  <div className="flex items-center gap-2 bg-card-border rounded-full px-3 py-1">
                    <span className="text-xs font-medium text-title">{product.quantity}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});

OrderDetailsModal.displayName = 'OrderDetailsModal';
